import React, {
    useEffect, useId, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog } from '../ui';
import { useUserSettings } from '../../contexts/userSettingsValue';

/**
 * My preferences (docs/decisions/role-presets.md), from the v2 user menu:
 *
 * - Starting layout: the role preset the user starts from ('' = as assigned
 *   by the admin). Writes `preset.choice` for the dealership in view.
 * - My margin floor: a personal floor for the unit margin tile, inside the
 *   dealership's allowed range; empty = use the dealership default.
 *   Written as `margin.floor_pct` (null = no personal value).
 *
 * Save sends the changed keys in one PUT; a 422 (out of range, unknown
 * preset) shows the server's message. With `can_save` false the dialog is
 * read-only.
 */

const PLAIN_NUMBER = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

const parseFloor = (text) => {
    const t = String(text ?? '').trim().replace(',', '.');
    if (t === '') return { value: null };
    if (!PLAIN_NUMBER.test(t)) return { error: 'Enter a number.' };
    return { value: Number(t) };
};

const fmtNum = (n) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
const fmtRange = (a, b) => `${fmtNum(a)}–${fmtNum(b)} %`;
const fmtPct = (n) => `${fmtNum(n)} %`;

/** The message a rejected save carries (422 detail string, list or object). */
export const preferenceErrorText = (err) => {
    const detail = err?.details?.detail ?? err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((d) => d?.msg || d?.message).filter(Boolean).join(' ') || 'The values were not accepted.';
    if (detail && typeof detail === 'object') {
        const msgs = Object.values(detail.errors || detail).flat().filter((m) => typeof m === 'string');
        if (msgs.length) return msgs.join(' ');
    }
    return err?.status === 422 ? 'The values were not accepted.' : 'Your preferences could not be saved. Please try again.';
};

export default function MyPreferencesDialog({ open, onClose }) {
    const ids = useId().replace(/:/g, '');
    const settings = useUserSettings();
    const {
        preset, presetsAvailable, margin, canSave, savePreferences,
    } = settings;
    const presets = presetsAvailable || [];
    const initialChoice = preset?.source === 'user' ? preset.id : '';
    const initialFloor = margin?.user_floor_pct ?? null;

    const [choice, setChoice] = useState(initialChoice);
    const [floorText, setFloorText] = useState(initialFloor == null ? '' : String(initialFloor));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const floorRef = useRef(null);
    const alertRef = useRef(null);

    // A fresh form every time it opens (and when the settings reload under it).
    useEffect(() => {
        if (!open) return;
        setChoice(initialChoice);
        setFloorText(initialFloor == null ? '' : String(initialFloor));
        setError(null);
        setSaving(false);
    }, [open, initialChoice, initialFloor]);
    useEffect(() => {
        if (error) alertRef.current?.focus();
    }, [error]);

    if (!open) return null;

    const readOnly = !canSave;
    const min = margin?.min_pct;
    const max = margin?.max_pct;
    const dealershipFloor = margin?.dealership_floor_pct;
    const parsed = parseFloor(floorText);
    const hasRange = typeof min === 'number' && typeof max === 'number';
    const outOfRange = (v) => hasRange && v !== null && (v < min || v > max);
    // QA P-2: the dealership may have narrowed the range after the user saved
    // a floor. Left untouched, that stored value is not an error (it is not
    // sent, so other fields still save); a notice says the dealership value
    // applies. Editing the field validates it as usual.
    const floorUntouched = !parsed.error && parsed.value === initialFloor;
    const storedOutOfRange = floorUntouched && outOfRange(initialFloor);
    let floorError = parsed.error || null;
    if (!floorError && !storedOutOfRange && outOfRange(parsed.value)) {
        floorError = `Enter a value between ${fmtPct(min)} and ${fmtPct(max)}, or use the dealership default.`;
    }

    const patch = {};
    if ((choice || '') !== (initialChoice || '')) patch['preset.choice'] = choice || null;
    if (margin && !parsed.error && parsed.value !== initialFloor) patch['margin.floor_pct'] = parsed.value;
    const dirty = Object.keys(patch).length > 0;
    const canSubmit = !readOnly && dirty && !floorError && !saving;

    const assignedName = preset?.source === 'admin' ? preset.name : null;
    // No preset at all: nothing is assigned, the system default applies (QA L5).
    // With the user's own pick the admin's assignment is not known here.
    const emptyChoiceLabel = (() => {
        if (assignedName) return `As assigned by your admin (${assignedName})`;
        if (!preset) return 'No preset assigned (system default)';
        return 'As assigned by your admin';
    })();
    const chosen = presets.find((p) => p.id === choice);

    const submit = async (e) => {
        e?.preventDefault();
        if (floorError) { floorRef.current?.focus(); return; }
        if (!canSubmit) return;
        setSaving(true);
        setError(null);
        try {
            await savePreferences(patch);
            setSaving(false);
            onClose();
        } catch (err) {
            setSaving(false);
            setError(preferenceErrorText(err));
        }
    };

    const footer = (
        <>
            <Button onClick={onClose}>{readOnly ? 'Done' : 'Cancel'}</Button>
            {!readOnly && (
                <Button variant="primary" onClick={submit} disabled={!canSubmit}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            )}
        </>
    );

    return (
        <Dialog open onClose={onClose} title="My preferences" footer={footer}>
            <form className="v2-prefs" onSubmit={submit} noValidate aria-label="My preferences">
                {readOnly && (
                    <p className="v2-prefs-note" role="note">
                        Your preferences can&apos;t be saved for this sign-in, so they are shown read-only.
                    </p>
                )}

                <section className="v2-prefs-sec" aria-labelledby={`${ids}-start-h`}>
                    <h3 id={`${ids}-start-h`}>Starting layout</h3>
                    {presets.length === 0 ? (
                        <p className="v2-prefs-hint">There are no role presets at this dealership yet.</p>
                    ) : (
                        <>
                            <label className="v2-prefs-fld" htmlFor={`${ids}-start`}>
                                <span>Start my inventory columns from</span>
                                <select
                                    id={`${ids}-start`}
                                    className="v2-sel"
                                    value={choice}
                                    disabled={readOnly}
                                    onChange={(e) => setChoice(e.target.value)}
                                >
                                    <option value="">
                                        {emptyChoiceLabel}
                                    </option>
                                    {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </label>
                            <p className="v2-prefs-hint">
                                {chosen?.description ? `${chosen.description} ` : ''}
                                Your own column changes stay; &ldquo;Reset to preset&rdquo; in the Columns menu applies the starting layout.
                            </p>
                        </>
                    )}
                </section>

                {margin && (
                    <section className="v2-prefs-sec" aria-labelledby={`${ids}-floor-h`}>
                        <h3 id={`${ids}-floor-h`}>My margin floor</h3>
                        <label className="v2-prefs-fld" htmlFor={`${ids}-floor`}>
                            <span>My margin floor</span>
                            <span className="v2-prefs-in">
                                <input
                                    id={`${ids}-floor`}
                                    ref={floorRef}
                                    className="v2-input"
                                    type="number"
                                    inputMode="decimal"
                                    min={min}
                                    max={max}
                                    step="any"
                                    placeholder={dealershipFloor != null ? String(dealershipFloor) : ''}
                                    value={floorText}
                                    readOnly={readOnly}
                                    aria-invalid={floorError ? 'true' : undefined}
                                    aria-describedby={`${ids}-floor-hint${storedOutOfRange ? ` ${ids}-floor-notice` : ''}${floorError ? ` ${ids}-floor-err` : ''}`}
                                    onChange={(e) => setFloorText(e.target.value)}
                                />
                                <span aria-hidden="true">%</span>
                            </span>
                        </label>
                        <p id={`${ids}-floor-hint`} className="v2-prefs-hint">
                            Dealership: {dealershipFloor != null ? fmtPct(dealershipFloor) : '—'}
                            {typeof min === 'number' && typeof max === 'number' && <> · allowed {fmtPct(min)} to {fmtPct(max)}</>}.
                            {' '}Gross margin below your floor shows amber on unit pages.
                            {parsed.value === null && !parsed.error && ' Using the dealership default.'}
                        </p>
                        {storedOutOfRange && (
                            <p id={`${ids}-floor-notice`} className="v2-prefs-notice" role="status">
                                Your margin floor of {fmtPct(initialFloor)} is outside the range your dealership
                                allows ({fmtRange(min, max)}). The dealership value
                                {' '}{dealershipFloor != null ? fmtPct(dealershipFloor) : ''} applies.
                            </p>
                        )}
                        {floorError && <p id={`${ids}-floor-err`} className="v2-prefs-err">{floorError}</p>}
                        {!readOnly && floorText.trim() !== '' && (
                            <button
                                type="button"
                                className="v2-prefs-link"
                                onClick={() => { setFloorText(''); floorRef.current?.focus(); }}
                            >
                                Use dealership default
                            </button>
                        )}
                    </section>
                )}

                {error && (
                    <p className="v2-prefs-alert" role="alert" tabIndex={-1} ref={alertRef}>{error}</p>
                )}
                {/* Enter in a field submits. */}
                <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
            </form>
        </Dialog>
    );
}

MyPreferencesDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};
